import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Mail, Loader2, CheckCircle, XCircle, AlertCircle, ChevronLeft, ChevronRight, Search, Trash2, Send, RefreshCw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_LABELS = {
  new_booking: { label: 'Nuova Prenotazione', color: 'bg-green-500/20 text-green-400' },
  confirmation: { label: 'Conferma', color: 'bg-accent/20 text-accent' },
  cancelled: { label: 'Annullamento', color: 'bg-red-500/20 text-red-500' },
  modified: { label: 'Modifica', color: 'bg-yellow-500/20 text-yellow-500' },
  modified_date: { label: 'Modifica Data', color: 'bg-amber-500/20 text-amber-400' },
  modified_time: { label: 'Modifica Orario', color: 'bg-orange-500/20 text-orange-400' },
  reminder_24h: { label: 'Promemoria 24h', color: 'bg-blue-500/20 text-blue-500' },
  reminder_2h: { label: 'Promemoria 2h', color: 'bg-purple-500/20 text-purple-500' },
  test_confirmation: { label: 'Test Conferma', color: 'bg-accent/10 text-accent/70' },
  test_cancelled: { label: 'Test Annullamento', color: 'bg-red-500/10 text-red-400' },
  test_modified: { label: 'Test Modifica', color: 'bg-yellow-500/10 text-yellow-400' },
  test_reminder_24h: { label: 'Test 24h', color: 'bg-blue-500/10 text-blue-400' },
  test_reminder_2h: { label: 'Test 2h', color: 'bg-purple-500/10 text-purple-400' },
};

const STATUS_ICONS = {
  sent: { icon: CheckCircle, color: 'text-emerald-400', label: 'Inviato' },
  delivered: { icon: CheckCircle, color: 'text-emerald-400', label: 'Consegnata' },
  blocked: { icon: XCircle, color: 'text-red-400', label: 'Bloccata' },
  spam: { icon: AlertCircle, color: 'text-orange-400', label: 'Segnalata spam' },
  failed: { icon: XCircle, color: 'text-red-400', label: 'Fallito' },
  not_configured: { icon: AlertCircle, color: 'text-yellow-500', label: 'Non configurato' },
};

const PAGE_SIZE = 20;

const AdminEmailLogSection = () => {
  const { getAuthHeaders } = useAuth();
  const [emails, setEmails] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [emailCount, setEmailCount] = useState(0);
  const [clearOpen, setClearOpen] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/admin/email-log/sync`, {}, { headers: getAuthHeaders() });
      const { checked, delivered, blocked, spam } = r.data;
      if (blocked > 0) toast.error(`${blocked} email bloccate da Brevo (su ${checked} controllate)`);
      else toast.success(`Controllate ${checked} email: ${delivered} consegnate${spam ? `, ${spam} spam` : ''}`);
      fetchEmails();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Errore nel controllo con Brevo');
    } finally { setSyncing(false); }
  };

  useEffect(() => { fetchEmails(); }, [page, filterType, searchTerm]);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, offset: page * PAGE_SIZE });
      if (filterType !== 'all') params.set('email_type', filterType);
      if (searchTerm) params.set('search', searchTerm);
      const res = await axios.get(`${API}/admin/email-log?${params}`, { headers: getAuthHeaders() });
      setEmails(res.data.emails || []);
      setTotal(res.data.total || 0);
      setEmailCount(res.data.email_count || 0);
    } catch (error) {
      console.error('Errore caricamento log notifiche:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleClear = async () => {
    try {
      await axios.delete(`${API}/admin/email-log`, { headers: getAuthHeaders() });
      toast.success('Storico notifiche svuotato');
      setClearOpen(false);
      setPage(0);
      fetchEmails();
    } catch (error) {
      toast.error('Errore durante la cancellazione');
    }
  };

  const handleTestBrevo = async () => {
    if (!testEmail) { toast.error('Inserisci un indirizzo email'); return; }
    setTestLoading(true);
    try {
      const res = await axios.post(`${API}/admin/notification-settings/test-brevo`, { email: testEmail }, { headers: getAuthHeaders() });
      toast.success(res.data.message || 'Email di test inviate!');
      fetchEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio test');
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="email-log-section">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h2 className="text-2xl sm:text-3xl font-bold">Storico Notifiche</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="secondary" className="text-xs"><Mail className="h-3 w-3 mr-1 inline" />{emailCount} email</Badge>
          <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing} data-testid="sync-email-status-btn">
            {syncing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />} Aggiorna stato consegna
          </Button>
          <Button variant="outline" size="sm" className="text-red-400 hover:text-red-300" onClick={() => setClearOpen(true)} data-testid="clear-log-btn">
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Svuota
          </Button>
        </div>
      </div>

      {/* Test Brevo Emails */}
      <Card className="card-elegant border-dashed border-accent/30" data-testid="test-brevo-card">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex-1">
              <p className="text-sm font-semibold mb-1">Test Email Brevo</p>
              <p className="text-xs text-muted-foreground">Invia 5 email di test (conferma, annullamento, modifica, promemoria 24h, promemoria 2h)</p>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Input
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="email@esempio.com"
                className="max-w-[220px]"
                data-testid="test-brevo-email-input"
              />
              <Button
                onClick={handleTestBrevo}
                disabled={testLoading}
                className="bg-accent text-accent-foreground hover:bg-accent/90 whitespace-nowrap"
                data-testid="test-brevo-btn"
              >
                {testLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                Test Brevo
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters Row */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
            placeholder="Cerca per nome, email, oggetto..." className="pl-10" data-testid="search-input" />
        </div>
        <Select value={filterType} onValueChange={(v) => { setFilterType(v); setPage(0); }}>
          <SelectTrigger className="w-[180px] h-9" data-testid="email-type-filter">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            <SelectItem value="new_booking">Nuova Prenotazione</SelectItem>
            <SelectItem value="confirmation">Conferma</SelectItem>
            <SelectItem value="cancelled">Annullamento</SelectItem>
            <SelectItem value="modified">Modifica Generale</SelectItem>
            <SelectItem value="modified_date">Modifica Data</SelectItem>
            <SelectItem value="modified_time">Modifica Orario</SelectItem>
            <SelectItem value="reminder_24h">Promemoria 24h</SelectItem>
            <SelectItem value="reminder_2h">Promemoria 2h</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Clear Confirmation Dialog */}
      <Dialog open={clearOpen} onOpenChange={setClearOpen}>
        <DialogContent className="max-w-sm" data-testid="clear-dialog">
          <DialogHeader><DialogTitle>Svuota Storico</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Sei sicuro di voler eliminare tutte le notifiche dallo storico? Questa azione non puo' essere annullata.</p>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setClearOpen(false)} className="flex-1">Annulla</Button>
            <Button onClick={handleClear} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-clear-btn">Svuota Tutto</Button>
          </div>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      ) : emails.length === 0 ? (
        <Card className="card-elegant">
          <CardContent className="p-12 text-center">
            <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-40" />
            <p className="text-muted-foreground">Nessuna notifica inviata</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-2" data-testid="email-list">
            {emails.map((em, i) => {
              const typeInfo = TYPE_LABELS[em.email_type] || { label: em.email_type, color: 'bg-secondary text-muted-foreground' };
              const statusInfo = STATUS_ICONS[em.status] || STATUS_ICONS.failed;
              const StatusIcon = statusInfo.icon;
              const sentDate = em.sent_at ? new Date(em.sent_at) : null;
              return (
                <Card key={em.id || i} className="card-elegant" data-testid={`email-item-${i}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex flex-col items-center gap-1 flex-shrink-0">
                          <StatusIcon className={`h-4 w-4 ${statusInfo.color}`} />
                          <Mail className="h-3.5 w-3.5 text-blue-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-semibold truncate">{em.subject}</p>
                            <Badge className={`text-[10px] px-2 ${typeInfo.color}`}>{typeInfo.label}</Badge>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                            <span>A: <span className="text-foreground/80">{em.to}</span></span>
                            {em.client_name && <span>Cliente: <span className="text-foreground/80">{em.client_name}</span></span>}
                            {em.status !== 'sent' && (
                              <Badge variant="outline" className={`text-[9px] px-1.5 ${statusInfo.color}`} data-testid={`email-status-${i}`}>{statusInfo.label}</Badge>
                            )}
                          </div>
                          {em.error && <p className="text-xs text-red-400 mt-1" data-testid={`email-error-${i}`}>{em.error}</p>}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground flex-shrink-0">
                        {sentDate && format(sentDate, 'dd MMM yyyy, HH:mm', { locale: it })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2" data-testid="email-pagination">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} data-testid="email-prev-page">
                <ChevronLeft className="h-4 w-4 mr-1" /> Precedente
              </Button>
              <span className="text-sm text-muted-foreground">Pagina {page + 1} di {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="email-next-page">
                Successiva <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminEmailLogSection;
